import React from 'react';
import { connect } from 'react-redux';
import { Col, Panel, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import ListingQuickUpdateModal from 'components/listing/quick_update/modal';
import ProjectCard from 'components/project/project_card';
import LeadsCard from './leads/card';
import PredictionsCard from './predictions_card';
import ActivityFeed from './activity_feed';
import { updateListing, addContact, removeContact, updateContactRole } from 'components/project/actions';
import { ProjectPropTypes } from 'components/project/prop_types';
import ContactSelector from 'components/project/contact_selector';
import ContactList from 'components/project/contacts_list';
import OpenTasks from './open_tasks';
import { showNewLeadModal, showLogTaskModal } from 'bundles/project/actions/index';
import CopyContactRoleModal from './copy_contact_role_modal';
import PageHeader from 'components/project/page_header';
import HelpCenterLink from 'components/help_center_link';
import { isFetchingLeads, shouldFetchLeads } from '../selectors/leads';

export class ProjectOverview extends React.Component {
  static propTypes = {
    addContact: PropTypes.func.isRequired,
    features: PropTypes.shape({
      backoffice: PropTypes.bool,
      crm: PropTypes.bool,
      leadManagement: PropTypes.bool,
      pipeline: PropTypes.bool,
      projectTasksActivities: PropTypes.bool
    }).isRequired,
    fetchingLeads: PropTypes.bool.isRequired,
    handleFilterChange: PropTypes.func.isRequired,
    helpCenterUrl: PropTypes.string,
    leads: PropTypes.array,
    leadStatuses: PropTypes.array,
    project: ProjectPropTypes.isRequired,
    removeContact: PropTypes.func.isRequired,
    showLogTaskModal: PropTypes.func.isRequired,
    showNewLeadModal: PropTypes.func.isRequired,
    showNewNoteModal: PropTypes.func.isRequired,
    showNewTaskModal: PropTypes.func.isRequired,
    updateContactRole: PropTypes.func.isRequired,
    updateListing: PropTypes.func.isRequired,
  };

  state = {
    selectedContactRole: null,
    showContactCopyModal: false,
    showContactEditModal: false,
    showListingEditModal: false
  };

  showContactCopyModal = (e) => {
    e.preventDefault();
    this.setState({ showContactCopyModal: true });
  }

  hideContactCopyModal = () => {
    this.setState({ showContactCopyModal: false });
  }

  showContactEditModal = (e, selectedContactRole = null) => {
    if (e) {
      e.preventDefault();
    }
    this.setState({ showContactEditModal: true, selectedContactRole });
  };

  hideContactEditModal = () => {
    this.setState({ showContactEditModal: false, selectedContactRole: null });
  };

  addContact = (contact) => {
    return this.props.addContact(
      this.props.project.id,
      contact.associatedModelId,
      contact.associatedModelType,
      contact.relation
    ).then(this.hideContactEditModal);
  }

  updateContact = (contact) => {
    this.props.updateContactRole(
      this.props.project.id,
      this.state.selectedContactRole.id,
      contact.relation
    ).then(this.hideContactEditModal);
  }

  removeContact = (contactRoleId) => {
    this.props.removeContact(this.props.project.id, contactRoleId);
  }

  hasProperty = () => {
    return !isEmpty(this.props.project.researchProperty);
  }

  hasListing = () => {
    return !isEmpty(this.props.project.listing);
  }

  showListingEditModal = () => {
    this.setState({ showListingEditModal: true });
  };

  handleListingUpdate = (listing) => {
    this.hideListingEditModal();
    this.props.updateListing(listing);
  }

  hideListingEditModal = () => {
    this.setState({ showListingEditModal: false });
  };

  renderPropertyLink(linkText, subText) {
    if (this.props.project.propertyLink) {
      return (
        <div>
          <a href={this.props.project.propertyLink} name="property-link">
            <strong>{linkText}</strong>
          </a>
          <div>{subText}</div>
        </div>
      );
    } else {
      return (
        <div>
          <strong>{linkText}</strong>
          <div>{subText}</div>
        </div>
      );
    }
  }

  renderPropertyName() {
    const addressInfo = this.props.project.researchProperty;

    if (this.props.project.name) {
      return this.renderPropertyLink(
        this.props.project.name,
        `${addressInfo.address} | ${addressInfo.city}, ${addressInfo.state} ${addressInfo.zip}`
      );
    } else {
      return this.renderPropertyLink(
        addressInfo.address,
        `${addressInfo.city}, ${addressInfo.state} ${addressInfo.zip}`
      );
    }
  }

  renderPropertyDetails() {
    if (this.hasProperty()) {
      return (
        <React.Fragment>
          <div className="text-muted">Property</div>
          <div className="details-item pt-1">
            <span className="fa-stack fa-lg">
              <i className="fas fa-circle fa-stack-2x details-icon"></i>
              <i className="fas fa-building fa-stack-1x fa-inverse"></i>
            </span>
            <div className="pl-4">
              { this.renderPropertyName() }
            </div>
          </div>
        </React.Fragment>
      );
    } else {
      return (
        <Panel className="panel-default dashed-border py-4 text-center">
          <Panel.Body>
            Add a Property
          </Panel.Body>
        </Panel>
      );
    }
  }

  render() {
    const { databaseContactRoles } = this.props.project;

    const overviewCardAction = (
      <React.Fragment>

        {databaseContactRoles && databaseContactRoles.length > 0 && (
          <a className="d-inline-block" href="#" name="show-contact-role-copy" onClick={this.showContactCopyModal}>
            <FontAwesomeIcon icon={faPlus} size="sm" /> Property Contacts
          </a>
        )}
        <a className="ml-3 d-inline-block" href="#" onClick={this.showContactEditModal}>
          <FontAwesomeIcon icon={faPlus} size="sm" /> New Contact
        </a>
      </React.Fragment>
    );

    const {
      condoListings,
      contacts,
      databases,
      editLink,
      leaseSpaceListings,
      listing,
      lotListings,
      userIsOutsideBroker
    } = this.props.project;

    const {
      features,
      fetchingLeads,
      handleFilterChange,
      leadStatuses,
      leads,
      showLogTaskModal,
      showNewLeadModal,
      showNewTaskModal
    } = this.props;

    const { selectedContactRole } = this.state;

    const headerActions = (
      <PageHeader.Actions>
        <HelpCenterLink helpCenterUrl={this.props.helpCenterUrl} linkText="Learn more about Projects"/>
      </PageHeader.Actions>
    );

    return (
      <div>
        <PageHeader actions={headerActions} title="Project Overview" />
        <Row className="w-100">
          <Col className="project-col" md={features.projectTasksActivities ? 4 : 12}>
            <ProjectCard
              className="dynamic-height overview-panel position-relative"
              name="Details"
              rightActions={[overviewCardAction]}
            >
              {this.props.features.crm &&
                <React.Fragment>
                  <Panel.Body>
                    {this.renderPropertyDetails()}
                  </Panel.Body>
                  {this.hasProperty() && <div className="border-top" />}
                </React.Fragment>
              }
              <ContactList
                contacts={contacts}
                showLogTaskModal={showLogTaskModal}
                showNewTaskModal={showNewTaskModal}
                onAddContactClicked={!databases && !features.crm ? this.showContactEditModal : null}
                onRemoveContact={this.removeContact}
              />
            </ProjectCard>
            <PredictionsCard
              condoListings={condoListings}
              editLink={editLink}
              features={features}
              leaseListings={leaseSpaceListings}
              listing={listing}
              lotListings={lotListings}
              showModal={this.showListingEditModal}
            />
            {this.props.features.leadManagement &&
              <LeadsCard
                fetchingLeads={fetchingLeads}
                handleFilterChange={handleFilterChange}
                leads={leads}
                leadStatuses={leadStatuses}
                showNewLeadModal={showNewLeadModal}
              />
            }
          </Col>
          {features.projectTasksActivities &&
            <Col className="project-col project-overview-col" md={8}>
              <OpenTasks showNewTaskModal={showNewTaskModal} />
              <ActivityFeed limitView linkToActivitiesTab={true} />
            </Col>
          }
        </Row>

        <CopyContactRoleModal
          show={this.state.showContactCopyModal}
          onClose={this.hideContactCopyModal}
        />

        {this.hasListing() &&
          <ListingQuickUpdateModal
            features={features}
            listing={listing}
            show={this.state.showListingEditModal}
            userIsOutsideBroker={userIsOutsideBroker}
            onCancel={this.hideListingEditModal}
            onSave={this.handleListingUpdate}
          />
        }

        <ContactSelector
          selectedContactRole={selectedContactRole}
          show={this.state.showContactEditModal}
          onCancel={this.hideContactEditModal}
          onSave={selectedContactRole ? this.updateContact : this.addContact}
        />
      </div>
    );
  }
}

const mapStateToProps = state => (
  {
    features: state.features,
    fetchingLeads: !!isFetchingLeads(state) || !!shouldFetchLeads(state),
    leads: isFetchingLeads(state) || shouldFetchLeads(state) ? [] : state.leads,
    project: state.project,
  }
);

export default connect(
  mapStateToProps,
  {
    updateListing,
    addContact,
    updateContactRole,
    removeContact,
    showNewLeadModal,
    showLogTaskModal
  }
)(ProjectOverview);
